import { PageHero } from "@/components/PageHero";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { PrimaryCTA, OutlineCTA, TextCTA } from "@/components/CTA";
import { Tick } from "@/components/Annotation";
import { SectionLabel, Tape, StickyNote } from "@/components/Notebook";
import { MethodologyTeaser } from "@/components/MethodologyTeaser";
import { ShareCaseCard } from "@/components/ShareCaseCard";
import { PageMeta } from "@/components/PageMeta";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";

// -----------------------------------------------------------------------------
// English-only assets — kept English on purpose (per site rule for stats and
// participant testimonial quotes)
// -----------------------------------------------------------------------------
const CASE_STATS = [
  { value: "17",    label: "Participants" },
  { value: "100%",  label: "Feedback participation" },
  { value: "5.0/5", label: "Facilitator rating" },
  { value: "4.8/5", label: "Workshop rating" },
];

const PARTICIPANT_QUOTES = [
  { q: "The facilitator was very engaging",                                                                                       role: "Participant · Recruitment team" },
  { q: "Great presentation and very helpful",                                                                                     role: "Participant · Recruitment team" },
  { q: "The practical part through the cases",                                                                                    role: "Participant · Recruitment team" },
  { q: "Engaging, to the point and resourceful under different circumstances",                                                    role: "Participant · Recruitment team" },
  { q: "Interactive activities. Passion. Respect. Politeness.",                                                                    role: "Participant · Recruitment team" },
  { q: "The workshop was interactive for most of its duration — a quality that is often missing from many workshops",              role: "Participant · Recruitment team" },
];

const VIMEO_ID = "1211881897";
const VIMEO_SRC = `https://player.vimeo.com/video/${VIMEO_ID}?badge=0&autopause=0&player_id=0&app_id=58479&title=0&byline=0&portrait=0`;
// Existing Pantelis case-study URL (Insights CMS) — never invent a new one.
const PANTELIS_HREF = `${ROUTES.caseStudies}/pantelis-papachristou`;

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function PresentationExcellence() {
  const { t } = useLang();
  const outcomesBullets = t("pce.outcomes_bullets") || [];
  const situations      = t("pce.situations_items") || [];
  const layers          = t("pce.layers_items") || [];
  const outcomeTypes    = t("pce.outcome_types") || [];
  const approach        = t("pce.approach_steps") || [];
  const caseWorked      = t("pce.case_worked") || [];
  const caseOutcome     = t("pce.case_outcome") || [];
  const formats         = t("pce.formats_items") || [];

  return (
    <>
      <PageMeta
        titleKey="meta.pce_title"
        descriptionKey="meta.pce_description"
        path={ROUTES.presentationExcellence}
      />
      <PageHero
        testId="pce-hero"
        eyebrow={t("pce.hero_eyebrow")}
        headline={[t("pce.hero_h1_a"), t("pce.hero_h1_b")]}
        emphasis={t("pce.hero_emphasis")}
        emphasisAccent="underline"
        intro={t("pce.hero_intro")}
        meta={
          <>
            {t("pce.hero_meta_line1")}
            <br />
            {t("pce.hero_meta_line2")}
            <br />
            <span className="text-mustard">{t("pce.hero_meta_line3")}</span>
          </>
        }
      >
        <PrimaryCTA href={ROUTES.bookCorporate} testId="pce-primary-cta" location="hero" targetAssetId="booking_corporate">
          {t("pce.cta_book")}
        </PrimaryCTA>
        <OutlineCTA href={ROUTES.contact} testId="pce-brief-cta" location="hero" targetAssetId="contact_request">
          {t("pce.cta_brief")}
        </OutlineCTA>
      </PageHero>

      {/* Belief */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10"
        data-testid="pce-belief"
      >
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-6">
              {t("pce.belief_eyebrow")}
            </p>
            <blockquote className="font-serif text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.06] tracking-tight">
              {t("pce.belief_h1")}
              <br />
              <span className="italic">{t("pce.belief_h2")}</span>
            </blockquote>
            <p className="mt-8 font-body text-lg text-ink-soft max-w-3xl mx-auto leading-relaxed">
              {t("pce.belief_body")}
            </p>
            <p className="mt-8 font-hand text-terracotta text-2xl md:text-3xl">
              {t("pce.belief_note")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* What changes — outcomes bullets */}
      <section className="relative z-10 py-24 md:py-32" data-testid="pce-what-changes">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("pce.what_changes_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("pce.what_changes_h1")}
              <br />
              <span className="italic">{t("pce.what_changes_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[52ch]">
              {t("pce.what_changes_body")}
            </p>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="relative bg-paper border border-ink/12 shadow-[6px_12px_44px_rgba(27,27,24,0.08)] p-8 md:p-10">
              <Tape className="absolute -top-4 left-8" rotate={-4} width={110} />
              <SectionLabel className="mb-6 block">{t("pce.what_changes_label")}</SectionLabel>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
                {outcomesBullets.map((o) => (
                  <li key={o} className="flex items-start gap-3">
                    <Tick className="w-4 h-4 mt-1 shrink-0" />
                    <span className="font-body text-ink leading-relaxed">{o}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Situations — the presentations your people actually give */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10"
        data-testid="pce-situations"
      >
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("pce.situations_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("pce.situations_h1")}
              <br />
              <span className="italic">{t("pce.situations_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[62ch]">
              {t("pce.situations_body")}
            </p>
          </Reveal>

          <StaggerGroup className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 md:gap-4">
            {situations.map((s, i) => (
              <StaggerItem key={s}>
                <div className="relative bg-paper border border-ink/12 p-5 h-full">
                  <span className="font-hand text-terracotta text-xl leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-2 font-serif text-lg md:text-xl text-ink leading-snug">{s}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Learn it. Rehearse it. Use it. — layer flow */}
      <section className="relative z-10 py-24 md:py-32" data-testid="pce-layers">
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("pce.layers_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("pce.layers_h1")}
              <br />
              <span className="italic">{t("pce.layers_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-ink-soft leading-relaxed max-w-[62ch]">
              {t("pce.layers_body")}
            </p>
          </Reveal>

          <StaggerGroup className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-14">
            {layers.map((step, i) => (
              <StaggerItem key={step.label}>
                <div className="relative bg-paper border border-ink/12 p-5 h-full">
                  <span className="font-hand text-terracotta text-xl leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-2 font-serif text-2xl text-ink leading-tight">{step.label}</p>
                  <p className="mt-1 font-hand text-pencil text-base">{step.note}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <Reveal>
            <div className="border-l-2 border-terracotta pl-6 max-w-2xl">
              <p className="font-serif italic text-2xl text-ink leading-snug">
                {t("pce.layers_quote_a")}
                <br />
                {t("pce.layers_quote_b")}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Designed around your outcome */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10"
        data-testid="pce-outcomes"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-2xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("pce.outcome_types_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("pce.outcome_types_h1")}
              <br />
              <span className="italic">{t("pce.outcome_types_h2")}</span>
            </h2>
          </Reveal>

          <StaggerGroup className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {outcomeTypes.map((p, i) => (
              <StaggerItem key={p.title}>
                <div className="relative bg-paper border border-ink/12 p-8 h-full shadow-[4px_8px_28px_rgba(27,27,24,0.06)]">
                  <span className="font-hand text-terracotta text-2xl leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-serif text-2xl md:text-3xl text-ink leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-4 font-body text-ink-soft leading-relaxed">{p.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Approach — 4 steps */}
      <section className="relative z-10 py-24 md:py-32" data-testid="pce-approach">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("pce.approach_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("pce.approach_h1")}
              <br />
              <span className="italic">{t("pce.approach_h2")}</span>
            </h2>
          </Reveal>

          <StaggerGroup className="lg:col-span-7 space-y-6">
            {approach.map((step, i) => (
              <StaggerItem key={step.label}>
                <div className="flex gap-6 items-start border-t border-ink/15 pt-6">
                  <span className="font-hand text-terracotta text-3xl leading-none shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl text-ink">{step.label}</h3>
                    <p className="mt-2 font-body text-ink-soft leading-relaxed">{step.body}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Featured case — Pantelis Papachristou */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10"
        data-testid="pce-featured-case"
      >
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("pce.case_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("pce.case_h1")}
              <br />
              <span className="italic">{t("pce.case_h2")}</span>
            </h2>
          </Reveal>

          <article className="grid lg:grid-cols-12 gap-8 lg:gap-14">
            <div className="lg:col-span-4">
              <p className="font-body text-xs uppercase tracking-[0.22em] text-ink-soft mb-3">
                {t("pce.case_context")}
              </p>
              <p className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                {t("pce.case_who")}
              </p>
              <p className="font-hand text-lg text-pencil mt-1">{t("pce.case_role")}</p>

              <blockquote className="mt-8 border-l-2 border-terracotta pl-5 font-serif italic text-xl text-ink leading-snug">
                &ldquo;{t("pce.case_testimonial")}&rdquo;
              </blockquote>
              <p className="mt-4 font-hand text-terracotta text-base">
                — {t("pce.case_testimonial_tag")}
              </p>
              <div className="mt-5">
                <TextCTA href={PANTELIS_HREF} testId="pce-case-pantelis-link">
                  {t("epd.pantelis_link")}
                </TextCTA>
              </div>
            </div>

            <div className="lg:col-span-8 grid md:grid-cols-2 gap-8 md:gap-10">
              <div>
                <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                  {t("pce.case_challenge_label")}
                </p>
                <p className="font-body text-ink-soft leading-relaxed mb-8">
                  {t("pce.case_challenge")}
                </p>

                <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                  {t("pce.case_worked_label")}
                </p>
                <ul className="space-y-2">
                  {caseWorked.map((w) => (
                    <li key={w} className="flex items-start gap-3">
                      <Tick className="w-4 h-4 mt-1 shrink-0" />
                      <span className="font-body text-ink leading-relaxed">{w}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="border-l-2 border-terracotta pl-5 mb-8">
                  <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                    {t("pce.case_outcome_label")}
                  </p>
                  <ul className="space-y-2">
                    {caseOutcome.map((o) => (
                      <li key={o} className="flex items-start gap-3">
                        <span className="font-hand text-terracotta text-lg leading-none mt-1">
                          →
                        </span>
                        <span className="font-body text-ink leading-relaxed">{o}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-paper-deep/50 border border-ink/12 p-6">
                  <p className="font-body text-xs uppercase tracking-[0.22em] text-ink-soft mb-3">
                    {t("pce.case_takeaway_label")}
                  </p>
                  <p className="font-serif italic text-lg md:text-xl text-ink leading-snug">
                    {t("pce.case_takeaway")}
                  </p>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* Corporate workshop credibility — ratings block + participant phrases */}
      <section
        className="relative z-10 py-24 md:py-32"
        data-testid="pce-workshop-credibility"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("pce.workshop_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("pce.workshop_h1")}
              <br />
              <span className="italic">{t("pce.workshop_h2")}</span>
            </h2>
          </Reveal>

          {/* Dark-ink ratings panel — kept English */}
          <Reveal>
            <div className="relative bg-ink text-paper px-6 md:px-12 py-12 md:py-14">
              <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-8 text-center">
                {t("pce.workshop_ratings_label")}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-10 md:gap-6">
                {CASE_STATS.map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="font-serif text-5xl md:text-6xl lg:text-7xl text-mustard leading-none">
                      {s.value}
                    </p>
                    <p className="mt-3 font-body text-xs md:text-sm uppercase tracking-[0.2em] text-paper/80">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
              <p className="mt-10 font-body text-sm md:text-base text-paper/80 max-w-3xl mx-auto text-center leading-relaxed">
                {t("pce.workshop_ratings_summary")}
              </p>
            </div>
          </Reveal>

          {/* Participant phrases — kept English */}
          <Reveal className="mt-14 md:mt-16">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-8 text-center">
              {t("pce.workshop_quotes_label")}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {PARTICIPANT_QUOTES.map((c, i) => (
                <figure
                  key={i}
                  className="relative bg-paper border border-ink/12 p-7 md:p-8 shadow-[4px_8px_24px_rgba(27,27,24,0.06)]"
                >
                  <span
                    aria-hidden="true"
                    className="absolute -top-4 -left-1 font-serif text-6xl text-terracotta/50 leading-none"
                  >
                    &ldquo;
                  </span>
                  <blockquote className="font-serif italic text-lg md:text-xl text-ink leading-snug">
                    {c.q}
                  </blockquote>
                  <figcaption className="mt-4 font-hand text-pencil text-base">
                    — {c.role}
                  </figcaption>
                </figure>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Video testimonial — Marilena Katsimi */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/40 border-y border-ink/10"
        data-testid="pce-marilena-video"
      >
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("pce.video_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("pce.video_h1")}
              <br />
              <span className="italic">{t("pce.video_h2")}</span>
            </h2>
          </Reveal>

          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-start">
            <Reveal className="lg:col-span-6">
              <div
                className="relative bg-paper border border-ink/12 shadow-[4px_8px_30px_rgba(18,20,24,0.10)] p-4 md:p-5 max-w-[420px] mx-auto lg:max-w-none lg:mx-0"
                data-testid="pce-video-testimonial"
              >
                <Tape className="absolute -top-4 left-10" rotate={-5} width={120} />
                <Tape className="absolute -top-4 right-10" rotate={5} width={120} />
                <div
                  className="relative w-full bg-ink/[0.04] border border-ink/10 overflow-hidden"
                  style={{ paddingBottom: "177.78%" }}
                >
                  <iframe
                    src={VIMEO_SRC}
                    title="Marilena Katsimi — video testimonial for InterAct Globally"
                    data-testid="pce-video-iframe"
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    loading="lazy"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
                <div className="flex items-start justify-between gap-4 pt-4 px-1">
                  <div>
                    <p className="font-serif text-lg md:text-xl text-ink italic leading-snug">
                      &ldquo;{t("landing.test_video_quote")}&rdquo;
                    </p>
                    <p className="font-body text-sm text-ink-soft mt-2">
                      {t("landing.test_video_author")}
                    </p>
                  </div>
                  <span className="font-hand text-base md:text-lg text-terracotta shrink-0 mt-1">
                    {t("landing.test_press_play")}
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-6">
              <p className="font-body text-xs uppercase tracking-[0.22em] text-ink-soft mb-3">
                Journalist · Live television
              </p>
              <p className="font-serif text-3xl md:text-4xl text-ink leading-tight">
                Marilena Katsimi
              </p>
              <p className="font-hand text-lg text-pencil mt-1">Journalist &amp; TV Presenter</p>

              <blockquote className="mt-8 border-l-2 border-terracotta pl-5 font-serif italic text-xl md:text-2xl text-ink leading-snug">
                &ldquo;Great presentations don&rsquo;t come from remembering every word. They come from trusting your preparation, your body and your ability to stay present.&rdquo;
              </blockquote>
              <p className="mt-4 font-hand text-terracotta text-base">— Key takeaway</p>

              <p className="mt-8 font-body text-ink-soft leading-relaxed max-w-[52ch]">
                Despite years of television experience, Marilena often relied on memorising her speeches and worried about losing her train of thought during live presentations. She wanted to feel present with her audience instead of constantly monitoring herself.
              </p>

              <div className="mt-8 bg-paper border border-ink/12 p-6">
                <p className="font-body text-xs uppercase tracking-[0.22em] text-terracotta mb-3">
                  The shift
                </p>
                <ul className="space-y-2">
                  {["Stopped relying on memorised scripts","Developed a more natural speaking flow","Learned to regulate her attention live on air","Stayed calm and connected — even when things went off-script"].map((o) => (
                    <li key={o} className="flex items-start gap-3">
                      <span className="font-hand text-terracotta text-lg leading-none mt-1">→</span>
                      <span className="font-body text-ink leading-relaxed">{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Formats */}
      <section
        className="relative z-10 py-24 md:py-32 bg-paper-deep/50 border-y border-ink/10"
        data-testid="pce-formats"
      >
        <div className="max-w-[1300px] mx-auto px-5 md:px-10">
          <Reveal className="mb-14 max-w-3xl">
            <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-5">
              {t("pce.formats_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-ink">
              {t("pce.formats_h1")}
              <br />
              <span className="italic">{t("pce.formats_h2")}</span>
            </h2>
          </Reveal>

          <ul className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {formats.map((f, i) => (
              <li
                key={f}
                className="relative bg-paper border border-ink/12 p-6 flex items-start gap-4"
              >
                <span className="font-hand text-terracotta text-xl leading-none">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <p className="font-serif text-lg md:text-xl text-ink leading-snug">{f}</p>
              </li>
            ))}
          </ul>

          <Reveal className="mt-10">
            <div className="max-w-2xl">
              <StickyNote rotate={-2} color="#F2EBDA">
                {t("pce.formats_note")}
              </StickyNote>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Methodology teaser */}
      <ShareCaseCard programmeTitle="Presentation &amp; Communication Excellence" testId="pce-share" />
      <MethodologyTeaser testId="pce-methodology-teaser" />

      {/* Final CTA */}
      <section
        className="relative z-10 py-24 md:py-32 bg-ink text-paper"
        data-testid="pce-final-cta"
      >
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 text-center">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-mustard mb-6">
              {t("pce.final_eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-paper">
              {t("pce.final_h1")}
              <br />
              <span className="italic">{t("pce.final_h2")}</span>
            </h2>
            <p className="mt-6 font-body text-lg text-paper/90 max-w-2xl mx-auto leading-relaxed">
              {t("pce.final_body")}
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex justify-center gap-5 flex-wrap">
            <PrimaryCTA href={ROUTES.bookCorporate} testId="pce-final-book-cta" location="final_cta" targetAssetId="booking_corporate">
              {t("pce.final_cta_book")}
            </PrimaryCTA>
            <OutlineCTA
              href={ROUTES.contact}
              testId="pce-final-brief-cta"
              location="final_cta"
              targetAssetId="contact_request"
              className="!border-paper/70 !text-paper hover:!border-mustard hover:!text-mustard"
            >
              {t("pce.final_cta_brief")}
            </OutlineCTA>
          </Reveal>
        </div>
      </section>
    </>
  );
}
